// Every word the call screen shows. English by default, Ukrainian when the
// device or browser prefers it - the same rule the Android native call screen
// follows (res/values-uk/call_strings.xml), so a call that starts on the lock
// screen and continues in the app reads the same in both.

const STRINGS = {
    en: {
        statusDialing: 'Calling…',
        statusRinging: 'Ringing…',
        statusIncoming: 'Incoming call',
        statusIncomingVideo: 'Incoming video call',
        statusConnecting: 'Connecting…',
        statusReconnecting: 'Reconnecting…',

        endEnded: 'Call ended',
        endCancelled: 'Call cancelled',
        endDeclined: 'Call declined',
        endBusy: 'Busy',
        endNoAnswer: 'No answer',
        endUnavailable: 'Not available',
        endFailed: 'Couldn’t connect',
        endDropped: 'Connection lost',
        endMicDenied: 'No microphone access',
        endMicMissing: 'No microphone found',
        endMicBusy: 'Microphone is in use',
        endAnsweredElsewhere: 'Answered on another device',
        endMissed: 'Missed call',

        detailFailed: 'Check your connection and try again.',
        detailMicDeniedApp: 'Allow GudHub to use the microphone in Settings.',
        detailMicDeniedBrowser: 'Allow this site to use the microphone in your browser settings.',
        detailMicMissing: 'Connect a microphone and try again.',
        detailMicBusy: 'Close the other app using the microphone and try again.',

        noticeMuted: 'Microphone off',
        noticeMicBlocked: 'They can’t hear you: no microphone access',

        mute: 'Mute',
        unmute: 'Unmute',
        speaker: 'Speaker',
        video: 'Video',
        soon: 'Soon',
        audioOutput: 'Audio output',
        routeEarpiece: 'Phone',
        routeSpeaker: 'Speaker',
        routeHeadset: 'Headphones',
        routeBluetooth: 'Bluetooth',

        accept: 'Accept',
        decline: 'Decline',
        hangUp: 'End call',
        close: 'Close',
        callAgain: 'Call again',
        openSettings: 'Open settings',
        settings: 'Settings',

        hintVideoSoon: 'Video calls are coming soon',
        hintRouteFailed: 'Couldn’t switch the audio output',
        hintCameraOn: 'Camera on',
        hintCameraOff: 'Camera off',

        dialogLabel: 'Call with {name}',
        unknownCaller: 'Unknown',
    },
    uk: {
        statusDialing: 'Виклик…',
        statusRinging: 'Очікування відповіді…',
        statusIncoming: 'Вхідний дзвінок',
        statusIncomingVideo: 'Вхідний відеодзвінок',
        statusConnecting: 'З’єднання…',
        statusReconnecting: 'Відновлення зв’язку…',

        endEnded: 'Дзвінок завершено',
        endCancelled: 'Дзвінок скасовано',
        endDeclined: 'Дзвінок відхилено',
        endBusy: 'Абонент зайнятий',
        endNoAnswer: 'Абонент не відповідає',
        endUnavailable: 'Абонент недоступний',
        endFailed: 'Помилка з’єднання',
        endDropped: 'Зв’язок перервано',
        endMicDenied: 'Немає доступу до мікрофона',
        endMicMissing: 'Мікрофон не знайдено',
        endMicBusy: 'Мікрофон зайнятий',
        endAnsweredElsewhere: 'Прийнято на іншому пристрої',
        endMissed: 'Пропущений дзвінок',

        detailFailed: 'Перевірте з’єднання й спробуйте ще раз.',
        detailMicDeniedApp: 'Дозвольте GudHub доступ до мікрофона в Налаштуваннях.',
        detailMicDeniedBrowser: 'Дозвольте цьому сайту доступ до мікрофона в налаштуваннях браузера.',
        detailMicMissing: 'Підключіть мікрофон і спробуйте ще раз.',
        detailMicBusy: 'Закрийте інший застосунок, що використовує мікрофон, і спробуйте ще раз.',

        noticeMuted: 'Мікрофон вимкнено',
        noticeMicBlocked: 'Вас не чути: немає доступу до мікрофона',

        mute: 'Мікрофон',
        unmute: 'Мікрофон',
        speaker: 'Гучномовець',
        video: 'Відео',
        soon: 'Скоро',
        audioOutput: 'Аудіовихід',
        routeEarpiece: 'Телефон',
        routeSpeaker: 'Гучномовець',
        routeHeadset: 'Навушники',
        routeBluetooth: 'Bluetooth',

        accept: 'Відповісти',
        decline: 'Відхилити',
        hangUp: 'Завершити дзвінок',
        close: 'Закрити',
        callAgain: 'Передзвонити',
        openSettings: 'Відкрити налаштування',
        settings: 'Налаштування',

        hintVideoSoon: 'Відеодзвінки з’являться незабаром',
        hintRouteFailed: 'Не вдалося перемкнути аудіовихід',
        hintCameraOn: 'Камеру увімкнено',
        hintCameraOff: 'Камеру вимкнено',

        dialogLabel: 'Дзвінок: {name}',
        unknownCaller: 'Невідомий',
    },
};

export function pickLanguage(languages = (typeof navigator !== 'undefined' && (navigator.languages || [navigator.language])) || []) {
    for(const language of languages) {
        const code = String(language || '').toLowerCase().split('-')[0];
        if(STRINGS[code]) return code;
    }
    return 'en';
}

// t('dialogLabel', { name: 'Ann' }) -> 'Call with Ann'
export function createTranslator(language = pickLanguage()) {
    const table = STRINGS[language] || STRINGS.en;
    return (key, params) => {
        const text = table[key] ?? STRINGS.en[key] ?? key;
        if(!params) return text;
        return text.replace(/\{(\w+)\}/g, (match, name) => (name in params ? String(params[name]) : match));
    };
}

export const CALL_SCREEN_STRINGS = STRINGS;
